import { flatten, propEq } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Grid } from '../../../../packages/kiti/components/structure/Grid'
import { getGenderContext } from '../../../../utils/helpers/gender'
import { useForm, useFormState } from '../../../../packages/kiti/components/form/form/FormHandler'
import { useModalData } from '../../../../components/modals/ModalsContext'
import { usePlaceTypes } from '../../../../utils/helpers/placeTypes'
import { isActionAllowedForEntity } from '../_helpers/allowedActions'
import EditableSentence from './EditableSentence'
import FrequencySentence from './frequency/FrequencySentence'
import RelativeSentence from './relative/RelativeSentence'
import TimeQuantitySentence from './relative/TimeQuantitySentence'

const useOptions = () => {
  const { t } = useTranslation('triggers')
  const [placeTypes] = usePlaceTypes()

  return [
    {
      id: 'service_order_insert',
      name: t('baseSentence.whenActivityCreated'),
      entity: 'service_order',
      event: 'insert'
    },
    {
      id: 'service_order_update',
      name: t('baseSentence.whenActivityUpdated'),
      entity: 'service_order',
      event: 'update'
    },
    {
      id: 'service_order_upsert',
      name: t('baseSentence.whenActivityCreatedOrUpdated'),
      entity: 'service_order',
      event: 'upsert'
    },
    { id: 'event_insert', name: t('baseSentence.whenActivityStatusChanged'), entity: 'event', event: 'insert' },

    {
      id: 'service_order_tag_assigned_upsert',
      name: t('baseSentence.whenActivityTagAssigned'),
      entity: 'service_order_tag_assigned',
      event: 'upsert'
    },
    {
      id: 'service_order_tag_removed_upsert',
      name: t('baseSentence.whenActivityTagRemoved'),
      entity: 'service_order_tag_removed',
      event: 'upsert'
    },

    {
      id: 'non_compliance_event_insert',
      name: t('baseSentence.whenNonComplianceEventCreated'),
      entity: 'non_compliance_event',
      event: 'insert'
    },
    {
      id: 'forms_non_compliance_event_insert',
      name: t('baseSentence.whenNonComplianceEventCreated'),
      entity: 'forms_non_compliance_event',
      event: 'insert'
    },

    {
      id: 'non_compliance_insert',
      name: t('baseSentence.whenNonComplianceGroupCreated'),
      entity: 'non_compliance',
      event: 'insert'
    },
    { id: 'period_day', name: t('periods.every_day'), entity: 'non_compliance', event: 'every_day', type: 'recurrent' },
    {
      id: 'period_week',
      name: t('periods.every_week'),
      entity: 'non_compliance',
      event: 'every_week',
      type: 'recurrent'
    },
    {
      id: 'period_month',
      name: t('periods.every_month'),
      entity: 'non_compliance',
      event: 'every_month',
      type: 'recurrent'
    },

    {
      id: 'form_reply_insert',
      name: t('baseSentence.whenFormReplied'),
      entity: 'form_reply',
      event: 'insert'
    },

    {
      id: 'service_order_reply_upsert',
      name: t('baseSentence.whenReplyEdited'),
      entity: 'service_order_reply',
      event: 'upsert'
    },

    { id: 'period_day', name: t('periods.every_day'), entity: 'service_order', event: 'every_day', type: 'recurrent' },
    {
      id: 'period_week',
      name: t('periods.every_week'),
      entity: 'service_order',
      event: 'every_week',
      type: 'recurrent'
    },
    {
      id: 'period_month',
      name: t('periods.every_month'),
      entity: 'service_order',
      event: 'every_month',
      type: 'recurrent'
    },

    {
      id: 'service_order_relative_before',
      name: t('relatives.relative_before'),
      entity: 'service_order',
      event: 'relative_before',
      type: 'relative'
    },
    {
      id: 'service_order_moment',
      name: t('relatives.relative_moment'),
      entity: 'service_order',
      event: 'relative_moment',
      type: 'relative'
    },
    {
      id: 'service_order_relative_after',
      name: t('relatives.relative_after'),
      entity: 'service_order',
      event: 'relative_after',
      type: 'relative'
    },

    ...flatten(
      placeTypes.map(type => [
        {
          id: `${type.key}_insert`,
          name: t('baseSentence.whenPlaceTypeCreated', { placeType: type.name }),
          entity: type.key,
          event: 'insert'
        },
        {
          id: `${type.key}_update`,
          name: t('baseSentence.whenPlaceTypeUpdated', { placeType: type.name }),
          entity: type.key,
          event: 'update'
        },
        {
          id: `${type.key}_upsert`,
          name: t('baseSentence.whenPlaceTypeCreatedOrUpdated', { placeType: type.name }),
          entity: type.key,
          event: 'upsert'
        },

        { id: 'period_day', name: t('periods.every_day'), entity: type.key, event: 'every_day', type: 'recurrent' },
        { id: 'period_week', name: t('periods.every_week'), entity: type.key, event: 'every_week', type: 'recurrent' },
        {
          id: 'period_month',
          name: t('periods.every_month'),
          entity: type.key,
          event: 'every_month',
          type: 'recurrent'
        }
      ])
    )
  ]
}

const BaseSentence = () => {
  const form = useForm()
  const { t } = useTranslation('triggers')
  const [placeTypes] = usePlaceTypes()
  const context = getGenderContext(form.entity)
  const { handleChange } = useFormState()
  const { entities } = useModalData() || {}
  const filteredOptions = useOptions().filter(
    option => option.entity === 'frequency' || (entities || []).includes(option.entity)
  )

  let label = `${t('when', { context })} ${t(
    'entities.' + form.entity,
    placeTypes.find(propEq('key', form.entity))?.name
  )} for ${t('events.' + form.event, {
    context
  })}`
  const isEver = form.event?.startsWith('every_')
  const isRelative = form.event?.startsWith('relative_')
  const isTagEvent = ['service_order_tag_assigned', 'service_order_tag_removed'].includes(form.entity)
  const isReplyEvent = form.entity === 'service_order_reply'

  if (isEver) label = `${t('periods.' + form.event)}`
  if (isRelative) label = `${t('relatives.' + form.event)}`
  // As duas entidades de tag são um evento inteiro só (não uma combinação entidade+evento como as
  // demais), então usam direto o nome amigável da própria opção em vez da sentença genérica acima.
  if (isTagEvent) {
    label =
      form.entity === 'service_order_tag_assigned'
        ? t('baseSentence.whenActivityTagAssigned')
        : t('baseSentence.whenActivityTagRemoved')
  }
  // Mesmo motivo do bloco de tags acima: "resposta editada" é um evento inteiro com rótulo
  // próprio, não uma combinação genérica de entidade+evento (que ficaria estranha: "resposta
  // da atividade criada ou atualizada").
  if (isReplyEvent) {
    label = t('baseSentence.whenReplyEdited')
  }

  return (
    <Grid gap={0.7} align="center">
      <TimeQuantitySentence />
      <EditableSentence
        label={label}
        value={{ id: `${form.entity}_${form.event}` }}
        onChange={option => {
          if (form.entity !== option.entity) {
            handleChange('conditions', [])
            handleChange('payload', {})
            // Ação que não vale para o novo gatilho (ex.: gatilho "resposta editada" só aceita
            // "Atualizar Atividade Filha") é limpa para o usuário escolher de novo.
            if (form.action && !isActionAllowedForEntity(form.action, option.entity)) {
              handleChange('action', undefined)
            }
          }
          handleChange('frequency', undefined)
          handleChange('entity', option.entity)
          handleChange('event', option.event)
          handleChange('type', option.type)
        }}
        options={filteredOptions}
        truncate={50}
        width={25}
      />
      <RelativeSentence />
      <FrequencySentence />
    </Grid>
  )
}

export default BaseSentence
